<script lang="ts" setup>
import { User02, Edit02 } from "utruckin-icons";
import { useUserStore } from "@/features/users/user.store";
import CommonButton from "@/components/common/CommonButton.vue";
import { ref } from "vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import { useNotification } from "@/utils/notification";

const usersStore = useUserStore();
const { pushNotification } = useNotification();

const password = ref<string>("");
const confirmPassword = ref<string>("");
const passwordModal = ref<boolean>(false);
const modalLoading = ref<boolean>(false);

const openModal = () => {
  password.value = "";
  confirmPassword.value = "";
  passwordModal.value = true;
};

const closeModal = () => {
  password.value = "";
  confirmPassword.value = "";
  passwordModal.value = false;
};

const saveModal = async () => {
  if (!(password.value.length > 5) || password.value !== confirmPassword.value)
    return;

  const result = await usersStore.changePassword(
    modalLoading,
    usersStore.userProfile?.id as string,
    {
      new_password: password.value,
    }
  );

  if (result) {
    pushNotification("Password changed successfully", "success");
    closeModal();
  }
};
</script>

<template>
  <div class="v-profile">
    <div class="body">
      <div class="body__profile">
        <div class="body__profile--icon">
          <User02 :size="20" />
        </div>
        <div class="body__profile--info">
          <div class="name">{{ usersStore.userProfile?.name }}</div>
        </div>
      </div>

      <div class="body__items">
        <div class="item">
          <div class="item__label">Email:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.email || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Phone number:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.phone_number || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Team:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.team_name || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Role:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.role || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Date of birth:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.date_of_birth || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Working since:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.working_since || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Default day off:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.default_day_off || "-" }}
          </div>
        </div>
        <div class="item">
          <div class="item__label">Monthly wage:</div>
          <div class="item__value">
            {{ usersStore.userProfile?.monthly_wage || "-" }}
          </div>
        </div>

        <!--        <div class="password">-->
        <!--          <div class="info">-->
        <!--            <div class="item__label">Password:</div>-->
        <!--            <div class="item__value">**********</div>-->
        <!--          </div>-->
        <!--          <div class="action">-->
        <!--            <Edit02 :size="20" />-->
        <!--          </div>-->
        <!--        </div>-->
      </div>

      <div class="action">
        <CommonButton label="Change password" @click="openModal" />
      </div>
    </div>

    <CommonModal v-model="passwordModal" title="Password change">
      <div class="new-modal">
        <CommonInput type="password" v-model="password" label="New password" />
        <CommonInput
          type="password"
          v-model="confirmPassword"
          label="Confirm password"
        />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton
            :is-disabled="
              !(password.length > 5) || password !== confirmPassword
            "
            type="primary"
            label="Save"
            @clicked="saveModal"
          />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style lang="scss" scoped>
.v-profile {
  padding: var(--base-space-16);
  background: var(--color-system-background);
  height: calc(100vh - 80px);
  overflow: auto;
  color: var(--color-label);

  .body {
    max-width: 841px;
    margin: 0 auto;

    &__profile {
      border-radius: var(--base-space-12);
      border: 1px solid var(--color-border);
      padding: var(--base-space-12);
      display: flex;
      gap: var(--base-space-8);
      align-items: center;

      &--icon {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        border: 1px solid var(--color-border);
        background: var(--color-secondary-system-background);
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .name {
        font-size: 14px;
        line-height: 20px;
        font-weight: 600;
      }
    }

    &__items {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: var(--base-space-8);
      margin-top: var(--base-space-8);

      .item {
        border: 1px solid var(--color-border);
        border-radius: var(--base-space-12);
        padding: var(--base-space-12);

        &__label {
          color: var(--color-label-secondary);
          font-size: 14px;
          line-height: 20px;
        }

        &__value {
          font-size: 14px;
          line-height: 20px;
          font-weight: 600;
        }
      }
    }

    .action {
      display: flex;
      align-items: center;
      justify-content: end;
      margin-top: 8px;
    }
  }
}

.new-modal {
  display: flex;
  flex-direction: column;
  gap: var(--base-space-16);
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}
</style>
